// Service Layer Client
// Typed API client for the Service Layer, aligned with the generated dist build.

import type {
  TenantConfig,
  TenantResolutionResponse,
} from './tenant'

export type { TenantConfig, TenantResolutionRequest, TenantResolutionResponse } from './tenant'

export type ListScanOrdersResponse = unknown

export interface ServiceLayerClientConfig {
  /**
   * Base URL of the Service Layer API, e.g. "https://api.appic-command.com"
   * or "http://localhost:3000".
   *
   * NOTE: Do not include a trailing slash; path segments are appended by the client.
   */
  baseUrl: string

  /**
   * Optional static bearer token. If both token and getToken are provided,
   * getToken takes precedence.
   */
  token?: string | null

  /**
   * Optional async token provider. This allows integration with auth systems
   * like Supabase without coupling this package to a specific auth client.
   */
  getToken?: () => Promise<string | null> | string | null
}

export class ServiceLayerClient {
  private readonly config: ServiceLayerClientConfig
  private readonly baseUrlForUrl: string

  constructor(config: ServiceLayerClientConfig) {
    this.config = config
    // URL() treats a base without trailing slash as a "file". We normalize once so that
    // relative paths (e.g. "v1/tenant/resolve") append correctly for bases like:
    // - https://portal.clanda.de/service/v1
    // - https://portal.clanda.de/service
    // - https://api.clanda.de/v1
    this.baseUrlForUrl = config.baseUrl.endsWith('/') ? config.baseUrl : `${config.baseUrl}/`
  }

  /**
   * Resolve tenant from hostname.
   * Calls GET /v1/tenant/resolve?host={host}
   */
  async resolveTenant(host: string): Promise<TenantConfig> {
    const token =
      typeof this.config.getToken === 'function'
        ? await this.config.getToken()
        : this.config.token

    const baseWithoutTrailingSlash = this.baseUrlForUrl.replace(/\/$/, '')
    const hasV1 = baseWithoutTrailingSlash.endsWith('/v1')
    // If caller already includes /v1 in baseUrl, append "tenant/resolve"; otherwise append "v1/tenant/resolve".
    const path = hasV1 ? 'tenant/resolve' : 'v1/tenant/resolve'
    const url = new URL(path, this.baseUrlForUrl)
    url.searchParams.set('host', host)

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Failed to resolve tenant: ${response.status} ${response.statusText}`)
    }

    const data = (await response.json()) as TenantResolutionResponse
    return data.tenant
  }

  /**
   * Get tenant by ID.
   * Calls GET /v1/tenants/{tenant_id}
   */
  async getTenant(tenantId: string): Promise<TenantConfig> {
    const token =
      typeof this.config.getToken === 'function'
        ? await this.config.getToken()
        : this.config.token

    const baseWithoutTrailingSlash = this.baseUrlForUrl.replace(/\/$/, '')
    const hasV1 = baseWithoutTrailingSlash.endsWith('/v1')
    const path = hasV1 ? `tenants/${tenantId}` : `v1/tenants/${tenantId}`
    const url = new URL(path, this.baseUrlForUrl)

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Failed to get tenant: ${response.status} ${response.statusText}`)
    }

    const data = (await response.json()) as TenantConfig
    return data
  }

  /**
   * List scan orders, optionally scoped to a tenant.
   * Calls GET /v1/scan-orders or GET /v1/tenants/{tenant_id}/scan-orders
   */
  async listScanOrders(tenantId?: string): Promise<ListScanOrdersResponse> {
    const token =
      typeof this.config.getToken === 'function'
        ? await this.config.getToken()
        : this.config.token

    const baseWithoutTrailingSlash = this.baseUrlForUrl.replace(/\/$/, '')
    const hasV1 = baseWithoutTrailingSlash.endsWith('/v1')

    const path = tenantId
      ? hasV1
        ? `tenants/${tenantId}/scan-orders`
        : `v1/tenants/${tenantId}/scan-orders`
      : hasV1
        ? 'scan-orders'
        : 'v1/scan-orders'

    const url = new URL(path, this.baseUrlForUrl)

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Failed to list scan orders: ${response.status} ${response.statusText}`)
    }

    const data = (await response.json()) as ListScanOrdersResponse
    return data
  }

  /**
   * Alias for listScanOrders (naming parity with other clients).
   */
  async getScanOrders(tenantId?: string): Promise<ListScanOrdersResponse> {
    return this.listScanOrders(tenantId)
  }
}
